import { type EventMeta, type SubeventMeta } from '../../interfaces';

export type QueryTarget =
  { kind: 'subevent'; subeventId: number } | { eventIds: number[]; kind: 'events' } | { kind: 'sports' };

export type ResolveQueryTargetInput = {
  eventIds?: number[];
  eventMetas?: EventMeta[];
  subeventId?: number;
  subeventMeta?: SubeventMeta;
};

const deriveSubeventId = (meta: SubeventMeta | undefined, now: number): number | null => {
  if (!meta) return null;
  // Live-aware selection: once startTime has passed use the in-play subevent, otherwise the
  // pre-match one. A missing/unparseable startTime is treated as not-yet-live (pre-match) — we
  // can't know the live state without it, and pre-match is the safe default (cx-api always
  // supplies startTime; this only guards malformed input). `?? fallback` then degrades to
  // whichever id is actually present when the preferred one is absent, and to null if neither is.
  const isLive = meta.startTime != null && now >= Date.parse(meta.startTime);
  const primary = isLive ? meta.inPlaySubeventId : meta.preMatchSubeventId;
  const fallback = isLive ? meta.preMatchSubeventId : meta.inPlaySubeventId;
  return primary ?? fallback ?? null;
};

export const resolveQueryTarget = (props: ResolveQueryTargetInput, now: number = Date.now()): QueryTarget => {
  const subeventId = props.subeventId ?? deriveSubeventId(props.subeventMeta, now);
  if (subeventId != null) return { kind: 'subevent', subeventId };

  const candidates = props.eventIds?.length ? props.eventIds : (props.eventMetas?.map(meta => meta.eventId) ?? []);
  const eventIds = candidates.filter((id): id is number => id != null);
  if (eventIds.length > 0) return { eventIds, kind: 'events' };

  return { kind: 'sports' };
};
